<template>
   <div class="news-carousel">
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6 px-4">
         <h3 class="text-3xl font-semibold text-white leading-tight">
            Product Insights
         </h3>
         <div v-if="articles.length > 1" class="flex items-center gap-3">
            <button
               type="button"
               class="news-carousel__nav-btn"
               aria-label="Previous article"
               @click="goPrev"
            >
               <i class="fas fa-chevron-left"></i>
            </button>
            <button
               type="button"
               class="news-carousel__nav-btn"
               aria-label="Next article"
               @click="goNext"
            >
               <i class="fas fa-chevron-right"></i>
            </button>
         </div>
      </div>

      <div
         class="relative overflow-hidden rounded-xl shadow-lg bg-blueGray-800 border border-blueGray-700"
         @mouseenter="onPanelEnter"
         @mouseleave="onPanelLeave"
      >
         <!-- Desktop height locks to image; mobile stacks naturally -->
         <div
            class="news-carousel__viewport relative overflow-hidden"
            :style="viewportStyle"
         >
            <transition :name="transitionName">
               <article
                  :key="current.id"
                  class="news-carousel__slide w-full"
               >
                  <!-- Image drives banner height on large screens -->
                  <div class="news-carousel__media bg-blueGray-900">
                     <img
                        ref="slideImg"
                        :src="current.image"
                        :alt="current.imageAlt"
                        class="news-carousel__image"
                        @load="syncHeight"
                     />
                  </div>

                  <!-- Copy: title scrolls with body; CTA only after reading to the end -->
                  <div class="news-carousel__copy">
                     <div class="news-carousel__scroll-wrap">
                        <div
                           ref="bodyEl"
                           class="news-carousel__body px-6 md:px-8 pt-6 md:pt-8 pb-8 text-blueGray-300 leading-relaxed"
                           :class="{ 'is-unlocked': unlocked }"
                           @scroll="onBodyScroll"
                        >
                           <p
                              v-if="current.eyebrow"
                              class="text-xs font-bold uppercase tracking-wider text-lightBlue-200 mb-2"
                           >
                              {{ current.eyebrow }}
                           </p>
                           <h4 class="text-xl md:text-2xl xl:text-3xl font-bold text-white leading-snug mb-4">
                              {{ current.title }}
                           </h4>

                           <p
                              v-for="(paragraph, i) in current.paragraphs"
                              :key="'p-' + i"
                              class="mb-4 text-base md:text-lg font-light"
                           >
                              {{ paragraph }}
                           </p>

                           <ul
                              v-if="current.bullets && current.bullets.length"
                              class="mb-4 space-y-2"
                           >
                              <li
                                 v-for="(bullet, i) in current.bullets"
                                 :key="'b-' + i"
                                 class="flex items-start gap-3 text-base md:text-lg font-light"
                              >
                                 <span
                                    class="mt-2 w-1.5 h-1.5 rounded-full bg-lightBlue-400 flex-shrink-0"
                                 ></span>
                                 <span>{{ bullet }}</span>
                              </li>
                           </ul>

                           <p
                              v-for="(paragraph, i) in current.closing"
                              :key="'c-' + i"
                              class="mb-4 text-base md:text-lg font-light"
                           >
                              <template v-if="typeof paragraph === 'string'">
                                 {{ paragraph }}
                              </template>
                              <template v-else>
                                 <span v-for="(part, pi) in paragraph.parts" :key="pi">
                                    <router-link
                                       v-if="part.to"
                                       :to="part.to"
                                       class="text-lightBlue-300 font-medium underline hover:text-lightBlue-200"
                                    >{{ part.text }}</router-link>
                                    <strong
                                       v-else-if="part.strong"
                                       class="font-semibold text-white"
                                    >{{ part.text }}</strong>
                                    <span v-else>{{ part.text }}</span>
                                 </span>
                              </template>
                           </p>

                           <p
                              v-if="current.question"
                              class="mb-6 text-lg md:text-xl font-semibold text-white italic"
                           >
                              {{ current.question }}
                           </p>

                           <!-- CTA sits at end of article — only reached by scrolling -->
                           <div
                              v-if="current.linkTo"
                              class="news-carousel__cta pt-2 pb-1"
                           >
                              <a
                                 v-if="isAnchorOrExternal(current.linkTo)"
                                 :href="current.linkTo"
                                 class="inline-flex items-center text-sm font-semibold text-lightBlue-300 hover:text-lightBlue-200 underline transition-colors"
                                 :target="isExternal(current.linkTo) ? '_blank' : null"
                                 :rel="isExternal(current.linkTo) ? 'noopener noreferrer' : null"
                              >
                                 {{ current.linkLabel || "Learn more" }}
                                 <i class="fas fa-arrow-right ml-2 text-xs"></i>
                              </a>
                              <router-link
                                 v-else
                                 :to="current.linkTo"
                                 class="inline-flex items-center text-sm font-semibold text-lightBlue-300 hover:text-lightBlue-200 underline transition-colors"
                              >
                                 {{ current.linkLabel || "Learn more" }}
                                 <i class="fas fa-arrow-right ml-2 text-xs"></i>
                              </router-link>
                           </div>
                        </div>

                        <!-- Fade + Read more until the reader unlocks scrolling -->
                        <div
                           v-if="needsReadMore && !unlocked"
                           class="news-carousel__fade"
                        >
                           <button
                              type="button"
                              class="news-carousel__read-more"
                              @click="unlockReading"
                           >
                              Read more
                              <i class="fas fa-chevron-down ml-2 text-xs"></i>
                           </button>
                        </div>
                     </div>
                  </div>
               </article>
            </transition>
         </div>
      </div>

      <!-- Progress / dots -->
      <div
         v-if="articles.length > 1"
         class="news-carousel__dots"
      >
         <button
            v-for="(article, i) in articles"
            :key="article.id"
            type="button"
            class="news-carousel__dot"
            :class="{ 'is-active': i === index }"
            :aria-label="'Show article ' + (i + 1)"
            @click="goTo(i)"
         ></button>
      </div>
   </div>
</template>

<script>
import stayInformedAlarms from "@/assets/img/news/stay-informed-with-alarms.jpg";
import datavanMpcFlexible from "@/assets/img/news/datavan-mpc-flexible-control.png";
import wellboreDiagrams from "@/assets/img/news/wellbore-diagrams.png";
import notTextbookRightDesign from "@/assets/img/news/not-textbook-right-design.jpg";

const DWELL_MS = 15000;
const LG_BREAKPOINT = 1024;

export default {
   name: "NewsCarousel",
   data() {
      return {
         index: 0,
         hovered: false,
         reading: false,
         unlocked: false,
         needsReadMore: false,
         panelHeight: null,
         transitionName: "news-slide-next",
         timerId: null,
         readingTimerId: null,
         articles: [
            {
               id: "stay-informed-alarms",
               image: stayInformedAlarms,
               imageAlt:
                  "WaveData Cloud centralized sensor channel alarms across well sites",
               eyebrow: "WaveData Cloud",
               title: "Missed alarms don't wait for your next site visit.",
               paragraphs: [
                  "Across a fleet of well sites, supervisors juggle calls, emergencies, approvals, daily reports, and paper logs—while a critical sensor channel alarm can sit unseen until someone physically checks the pad. By then, you're not managing a channel; you're managing downtime, HSE risk, and a scramble to reconstruct what happened.",
                  "Centralized sensor channel alarm management changes that. When every field-equipped channel—pressure, temp, flow, status—feeds one consistent view in the cloud, field ops supervisors can:",
               ],
               bullets: [
                  "See active and acknowledged alarms across sites in one place",
                  "Spot patterns before they become incidents",
                  "Align crews on priority instead of chasing noise",
               ],
               closing: [
                  {
                     parts: [
                        { text: "Less time hunting. More time deciding.", strong: true },
                     ],
                  },
                  {
                     parts: [
                        { text: "With " },
                        { text: "WaveData Cloud", to: "/wavedata" },
                        {
                           text: ", you can set up your own alarms on any channel, equipment type, or combined logic to easily monitor specific behaviors or conditions—so you have immediate information when something occurs and don't miss critical actions.",
                        },
                     ],
                  },
               ],
               linkTo: "/wavedata",
               linkLabel: "Explore WaveData Cloud",
            },
            {
               id: "datavan-mpc-flexible",
               image: datavanMpcFlexible,
               imageAlt:
                  "Datavan MPC pump control interface showing flexible multi-pump layout",
               eyebrow: "Datavan MPC",
               title: "Control should adapt to your operation, not the other way around.",
               paragraphs: [
                  "We've redesigned the Datavan MPC to be the most flexible control interface on the frac pad. Whether you're running a high-density 4K setup, a traditional multi-screen layout, or even a mobile device, the new interface scales to fit your resolution and workflow.",
                  "Need to monitor 5 pumps in high detail—or 20 pumps at a glance? Customize exactly how many pumps appear on screen without losing situational awareness.",
               ],
               bullets: [
                  "Scales across 4K, multi-screen, and mobile layouts",
                  "Customize pump density for detail or fleet overview",
                  "Run PumpLink7 alongside legacy AMI pumps in the same spread",
               ],
               closing: [
                  {
                     parts: [
                        { text: "You don't have to choose between old and new. " },
                        { text: "Datavan MPC", to: "/mpc" },
                        { text: " bridges the gap—running latest " },
                        { text: "PumpLink7", to: "/pumplink7" },
                        { text: " hardware alongside your legacy AMI pumps together." },
                     ],
                  },
                  {
                     parts: [
                        {
                           text: "All the core features you rely on—precise group control and individual pump overrides—are still at your fingertips, now in a cleaner, faster, and more intuitive layout.",
                        },
                     ],
                  },
                  {
                     parts: [
                        { text: "Efficiency without the equipment trade-off.", strong: true },
                     ],
                  },
               ],
               linkTo: "/mpc",
               linkLabel: "Explore Datavan MPC",
            },
            {
               id: "wellbore-diagrams",
               image: wellboreDiagrams,
               imageAlt:
                  "Wellbore diagram with live concentration, bottom-hole calculations, and arrival times",
               eyebrow: "Datavan MPC",
               title: "New wellbore diagrams. Built for the pad—not the brochure.",
               paragraphs: [
                  "Our latest DataVan software now delivers a clearer downhole picture for faster decisions and better stage control:",
               ],
               bullets: [
                  "Bottom-hole calculations in real time",
                  "Live concentration visualized down the wellbore",
                  "Predicted arrival times to the perforations",
               ],
               closing: [
                  {
                     parts: [
                        { text: "Clearer downhole picture. Faster decisions. Better stage control.", strong: true },
                     ],
                  },
                  {
                     parts: [
                        { text: "And this is just one piece of the broader multi-pump control and DataVan suite we're building—designed to replace, and outperform, our proven " },
                        { text: "DVCommand", to: "/dvcommand" },
                        { text: " platform. Explore the redesigned " },
                        { text: "Datavan MPC", to: "/mpc" },
                        { text: " to see more." },
                     ],
                  },
               ],
               linkTo: "/mpc",
               linkLabel: "Explore Datavan MPC",
            },
            {
               id: "not-textbook-right-design",
               image: notTextbookRightDesign,
               imageAlt:
                  "Frac pad control challenges and AMI solutions with redundant views and remote UIs",
               eyebrow: "Frac Operations",
               title: "Real frac operations aren't textbook—and neither are the control problems.",
               paragraphs: [
                  "If you run a pumpdown operation or a full frac spread, the control problems that cost you hours rarely look like textbook issues. They're the ones your crew already knows:",
               ],
               bullets: [
                  "Comms drop mid-stage — HMI goes stale or the network drops a controller. Who still has command? Which unit is still responding? Supervisors end up coordinating by radio while someone walks the iron.",
                  "Displays that don't survive the pad — vibration loosens displays, cracks glass, or kills a touchscreen right when you need setpoint changes.",
                  "Sunlight vs. the screen — midday glare turns a good HMI into guesswork: wrong button, slow reaction, or an operator who stops trusting the interface.",
                  "Spread health is still tribal knowledge — with a handful of pumps, \"is the spread healthy?\" often means radio checks and walking units—not one clear view of pressures, rates, faults, and who's lagging.",
                  "Training gaps show up under pressure — new operators know the screens in the shop; on location, under noise and time pressure, they don't know the failover path when a screen dies or a link drops.",
               ],
               closing: [
                  {
                     parts: [
                        {
                           text: "None of this is exotic controls theory. It's frac site reality: keep command clear, keep the interface usable, and give supervisors a spread-level picture before the radio traffic spikes.",
                        },
                     ],
                  },
                  {
                     parts: [
                        { text: "The right design solves it.", strong: true },
                        {
                           text: " Redundant views, supervisor stations, and easy-to-use remote UIs—on site and in the cloud—are what we build into ",
                        },
                        { text: "Datavan MPC", to: "/mpc" },
                        { text: ", " },
                        { text: "WaveData Cloud", to: "/wavedata" },
                        { text: ", and the broader AMI controls suite to help solve these common frac site problems." },
                     ],
                  },
               ],
               linkTo: "#contact",
               linkLabel: "Talk with our team",
            },
         ],
      };
   },
   computed: {
      current() {
         return this.articles[this.index] || this.articles[0];
      },
      // Lock desktop viewport to measured image height so transitions don't collapse.
      viewportStyle() {
         if (this.panelHeight && this.isLargeScreen()) {
            return { height: `${this.panelHeight}px` };
         }
         return null;
      },
      canAutoplay() {
         return (
            this.articles.length > 1 &&
            !this.hovered &&
            !this.reading &&
            !this.unlocked
         );
      },
   },
   watch: {
      index() {
         this.reading = false;
         this.unlocked = false;
         this.$nextTick(() => {
            this.syncHeight();
            this.measureOverflow();
            const body = this.$refs.bodyEl;
            if (body) body.scrollTop = 0;
         });
         this.restartTimer();
      },
      canAutoplay(val) {
         if (val) this.restartTimer();
         else this.clearTimer();
      },
   },
   mounted() {
      this.$nextTick(() => {
         this.syncHeight();
         this.measureOverflow();
      });
      this.restartTimer();
      window.addEventListener("resize", this.onResize);
   },
   beforeUnmount() {
      this.clearTimer();
      this.clearReadingTimer();
      window.removeEventListener("resize", this.onResize);
   },
   methods: {
      isLargeScreen() {
         return window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`).matches;
      },
      clearTimer() {
         if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
         }
      },
      clearReadingTimer() {
         if (this.readingTimerId) {
            clearTimeout(this.readingTimerId);
            this.readingTimerId = null;
         }
      },
      restartTimer() {
         this.clearTimer();
         if (!this.canAutoplay) return;
         this.timerId = setTimeout(() => this.goNext(), DWELL_MS);
      },
      goTo(i, direction) {
         if (i === this.index || i < 0 || i >= this.articles.length) return;
         const dir = direction || (i > this.index ? "next" : "prev");
         this.transitionName =
            dir === "next" ? "news-slide-next" : "news-slide-prev";
         this.index = i;
      },
      goNext() {
         const next = (this.index + 1) % this.articles.length;
         this.goTo(next, "next");
      },
      goPrev() {
         const prev =
            (this.index - 1 + this.articles.length) % this.articles.length;
         this.goTo(prev, "prev");
      },
      // Hash (#contact) or absolute URL — use <a>; product routes use router-link.
      isAnchorOrExternal(to) {
         return typeof to === "string" && (to.startsWith("#") || this.isExternal(to));
      },
      isExternal(to) {
         return typeof to === "string" && /^https?:\/\//i.test(to);
      },
      onPanelEnter() {
         this.hovered = true;
      },
      onPanelLeave() {
         this.hovered = false;
      },
      unlockReading() {
         this.unlocked = true;
         this.reading = true;
         this.$nextTick(() => {
            const body = this.$refs.bodyEl;
            if (body) body.focus({ preventScroll: true });
         });
      },
      // Pause autoplay while the reader is scrolling the unlocked article.
      onBodyScroll() {
         if (!this.unlocked) return;
         this.reading = true;
         this.clearReadingTimer();
         this.readingTimerId = setTimeout(() => {
            this.reading = false;
         }, 4000);
      },
      onResize() {
         this.syncHeight();
         this.measureOverflow();
      },
      // Match banner height to the article image on desktop.
      syncHeight() {
         const img = this.$refs.slideImg;
         if (!img) return;
         if (this.isLargeScreen() && img.offsetHeight) {
            this.panelHeight = img.offsetHeight;
         } else {
            this.panelHeight = null;
         }
         this.$nextTick(() => this.measureOverflow());
      },
      // Show Read more only when content overflows the visible pane.
      measureOverflow() {
         const el = this.$refs.bodyEl;
         if (!el) {
            this.needsReadMore = false;
            return;
         }
         this.needsReadMore = el.scrollHeight > el.clientHeight + 8;
      },
   },
};
</script>

<style scoped>
.news-carousel__nav-btn {
   width: 3rem;
   height: 3rem;
   border-radius: 9999px;
   background: #1e293b;
   border: 1px solid #475569;
   color: #e2e8f0;
   font-size: 1rem;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease,
      color 0.2s ease, box-shadow 0.2s ease;
}

.news-carousel__nav-btn:hover {
   transform: scale(1.12);
   background: #334155;
   border-color: #7dd3fc;
   color: #e0f2fe;
   box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.2);
}

.news-carousel__nav-btn:active {
   transform: scale(1.04);
}

.news-carousel__dots {
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 0.5rem;
   margin-top: 1.25rem;
   padding: 0 1rem;
}

.news-carousel__dot {
   width: 0.625rem;
   height: 0.625rem;
   border-radius: 9999px;
   background: #64748b;
   border: none;
   padding: 0;
   cursor: pointer;
   transition: width 0.25s ease, background-color 0.25s ease, transform 0.2s ease;
}

.news-carousel__dot:hover {
   background: #94a3b8;
   transform: scale(1.15);
}

.news-carousel__dot.is-active {
   width: 2rem;
   background: #38bdf8;
}

.news-carousel__dot.is-active:hover {
   transform: none;
}

.news-carousel__slide {
   display: flex;
   flex-direction: column;
}

.news-carousel__image {
   display: block;
   width: 100%;
   height: auto;
}

.news-carousel__copy {
   display: flex;
   flex-direction: column;
   min-width: 0;
   min-height: 0;
   flex: 1 1 auto;
}

.news-carousel__scroll-wrap {
   position: relative;
   flex: 1 1 auto;
   min-height: 0;
   display: flex;
   flex-direction: column;
}

/* Locked: no scrollbar until Read more is clicked */
.news-carousel__body {
   flex: 1 1 auto;
   max-height: 16rem;
   overflow-y: hidden;
   min-height: 0;
}

.news-carousel__body.is-unlocked {
   overflow-y: auto;
   scrollbar-width: thin;
   scrollbar-color: #64748b transparent;
}

.news-carousel__body.is-unlocked::-webkit-scrollbar {
   width: 6px;
}
.news-carousel__body.is-unlocked::-webkit-scrollbar-thumb {
   background: #64748b;
   border-radius: 999px;
}
.news-carousel__body.is-unlocked::-webkit-scrollbar-track {
   background: transparent;
}

.news-carousel__fade {
   position: absolute;
   left: 0;
   right: 0;
   bottom: 0;
   height: 5.5rem;
   display: flex;
   align-items: flex-end;
   justify-content: center;
   padding-bottom: 0.85rem;
   background: linear-gradient(
      to bottom,
      rgba(30, 41, 59, 0),
      rgba(30, 41, 59, 0.88) 45%,
      #1e293b 100%
   );
   pointer-events: none;
}

.news-carousel__read-more {
   pointer-events: auto;
   font-size: 0.875rem;
   font-weight: 600;
   color: #e0f2fe;
   background: transparent;
   border: 1px solid #38bdf8;
   border-radius: 9999px;
   padding: 0.4rem 1rem;
   transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.news-carousel__read-more:hover {
   background: rgba(56, 189, 248, 0.15);
   color: #fff;
   transform: translateY(-1px);
}

@media (min-width: 1024px) {
   .news-carousel__viewport {
      overflow: hidden;
   }

   .news-carousel__slide {
      flex-direction: row;
      height: 100%;
      min-height: 0;
      overflow: hidden;
   }

   .news-carousel__media {
      width: 41.666667%;
      flex-shrink: 0;
      overflow: hidden;
   }

   .news-carousel__copy {
      width: 58.333333%;
      height: 100%;
      min-height: 0;
      overflow: hidden;
   }

   .news-carousel__body {
      flex: 1 1 0;
      max-height: none;
      height: auto;
   }

   /* Absolute only during slide transitions so the image-locked height stays stable */
   .news-slide-next-enter-active,
   .news-slide-next-leave-active,
   .news-slide-prev-enter-active,
   .news-slide-prev-leave-active {
      transition: transform 0.65s ease, opacity 0.65s ease;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
   }

   .news-slide-next-leave-active,
   .news-slide-prev-leave-active {
      z-index: 1;
   }

   .news-slide-next-enter-active,
   .news-slide-prev-enter-active {
      z-index: 2;
   }

   .news-slide-next-enter-from {
      transform: translateX(100%);
      opacity: 0;
   }
   .news-slide-next-leave-to {
      transform: translateX(-100%);
      opacity: 0;
   }

   .news-slide-prev-enter-from {
      transform: translateX(-100%);
      opacity: 0;
   }
   .news-slide-prev-leave-to {
      transform: translateX(100%);
      opacity: 0;
   }
}

@media (max-width: 1023px) {
   .news-slide-next-enter-active,
   .news-slide-next-leave-active,
   .news-slide-prev-enter-active,
   .news-slide-prev-leave-active {
      transition: opacity 0.4s ease;
   }

   .news-slide-next-enter-from,
   .news-slide-prev-enter-from {
      opacity: 0;
   }
   .news-slide-next-leave-to,
   .news-slide-prev-leave-to {
      opacity: 0;
   }
}
</style>
